/**
 * Persistence (plan §3.1): the project file written by the service is authoritative
 * (`PUT /api/projects/:id` with If-Match, debounced after every store event); the browser
 * copy is a validated AutosaveSnapshot for recovery, offered on load only when newer and
 * different, and cleared with a message when it is corrupt.
 */
import type { AutosaveSnapshot, Project, Revision } from '@/domain';
import {
  createStore, hashOf, loadProfiles, SCHEMA_VERSION, validateSnapshot,
} from '@/domain';
import { decideFit, isFitResult } from '@/families/fit-schema';
import { FAMILY_IDS } from '@/families/registry';
import { api, ApiError, type ProjectFrom, type ServerProject } from './api';
import { announce, toast } from './announce';
import { debounce, fmtDate, fmtTime } from './format';
import { recogniseFacts } from './facts';
import { app, isStep, onStoreEvent, setStore, ui, type AnalysisState, type RefImage, type Theme } from './state';
import { go } from './navigation';

const LS_LAST = 'cutlist-maker:last-project';
const LS_THEME = 'cutlist-maker:theme';
export const browserCopyKey = (id: string): string => `cutlist-maker:autosave:${id}`;

let etag: string | null = null;
let serverProject: ServerProject | null = null;
let loading = false;
let chain: Promise<void> = Promise.resolve();

const lsGet = (k: string): string | null => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k: string, v: string): boolean => { try { localStorage.setItem(k, v); return true; } catch { return false; } };
const lsDel = (k: string): void => { try { localStorage.removeItem(k); } catch { /* ignore */ } };

// ---------------------------------------------------------------------------------------------
// Project <-> state
// ---------------------------------------------------------------------------------------------
function referenceEvidence(): Project['reference'] {
  const r = ui.ref;
  if (!r.images.length && !r.url && !r.text) return serverProject?.reference ?? null;
  const facts = ui.analysis.proposal?.evidence.textSpans.length
    ? ui.analysis.proposal.evidence.textSpans.map((t) => ({ kind: t.kind, value: t.text, unit: null, quotedText: t.text, span: t.span }))
    : recogniseFacts(r.text).map((f) => ({ kind: f.field, value: f.value, unit: f.unit, quotedText: f.text, span: f.span }));
  return {
    schemaVersion: SCHEMA_VERSION,
    images: r.images.filter((i) => /^[0-9a-f]{64}$/.test(i.sha256)).map((i) => ({ id: i.id, path: i.path ?? i.src, sha256: i.sha256, rightsBasis: i.rightsBasis || 'Not stated' })),
    sourceUrl: r.url,
    pastedText: r.text,
    publishedFacts: facts,
    imageEstimates: serverProject?.reference?.imageEstimates ?? [],
    unknowns: serverProject?.reference?.unknowns ?? [],
  };
}

function analysisRecord(): Project['analysis'] {
  const a = ui.analysis;
  const status = a.status === 'idle' ? 'none' : a.status === 'running' ? (ui.empty ? 'none' : 'done') : a.status;
  const prev = serverProject?.analysis;
  return {
    status,
    ranAt: a.ranAt ?? null,
    proposalAt: a.proposalAt ?? null,
    ...(a.proposal ? { proposal: a.proposal } : {}),
    ...(a.refusal ? { refusal: a.refusal } : {}),
    rawResponsePath: a.rawResponsePath ?? prev?.rawResponsePath ?? null,
    usage: a.usage ?? prev?.usage ?? null,
  };
}

export function buildProject(savedAt = new Date().toISOString(), { historyLimit }: { historyLimit?: number } = {}): Project {
  const s = app.store;
  const snapshot = s.snapshot({ savedAt });
  const cap = <T>(a: T[]) => (historyLimit != null ? a.slice(-historyLimit) : a);
  return {
    schemaVersion: SCHEMA_VERSION,
    name: s.design.appearance?.name || 'Untitled bookcase',
    reference: referenceEvidence(),
    analysis: analysisRecord(),
    working: {
      parentRevisionId: s.currentRevisionId as Revision['id'] | null,
      design: s.design, fields: s.fields, dirty: s.isDirty,
      undoStack: cap(snapshot.state.undoStack), redoStack: cap(snapshot.state.redoStack),
    },
    revisions: s.revisions,
    exports: s.exports,
    savedAt,
  };
}

function refFromProject(id: string, p: ServerProject): RefImage[] {
  return (p.reference?.images ?? []).map((im, i) => ({
    id: im.id, src: api.imageUrl(id, im.id), thumb: api.thumbUrl(id, im.id), path: im.path,
    alt: `Reference photo ${i + 1}`, sha256: im.sha256, rightsBasis: im.rightsBasis,
  }));
}
function analysisFromProject(p: ServerProject): AnalysisState {
  const a = p.analysis;
  const status = a.status === 'none' || a.status === 'running' ? 'idle' : a.status;
  const d = isFitResult(a.proposal) ? decideFit(a.proposal, FAMILY_IDS) : null;
  return {
    status, ranAt: a.ranAt, proposalAt: a.proposalAt, proposal: a.proposal ?? null, refusal: a.refusal ?? null,
    fit: d?.kind === 'proposal' ? { familyId: d.familyId, confidence: d.confidence, flagged: d.flagged, runnersUp: d.runnersUp } : null,
    rawResponsePath: a.rawResponsePath, usage: a.usage,
  };
}

// ---------------------------------------------------------------------------------------------
// Saving
// ---------------------------------------------------------------------------------------------
function writeBrowserCopy(savedAt: string): void {
  if (!ui.projectId) return;
  const snap: AutosaveSnapshot = app.store.snapshot({ savedAt });
  lsSet(browserCopyKey(ui.projectId), JSON.stringify(snap));
}

async function doSave(): Promise<void> {
  const id = ui.projectId;
  if (!id || loading) return;
  const savedAt = new Date().toISOString();
  let body = buildProject(savedAt);
  try {
    let r;
    try {
      r = await api.putProject(id, body, etag);
    } catch (e) {
      if (!(e instanceof ApiError) || e.status !== 412) throw e;
      // Someone else (the service: intake, exports) wrote the file: merge and retry once.
      const fresh = await api.getProject(id);
      serverProject = fresh.project;
      etag = fresh.etag;
      body = mergeServer(buildProject(savedAt), fresh.project);
      r = await api.putProject(id, body, etag);
    }
    etag = r.etag ?? etag;
    serverProject = r.project ?? serverProject;
    if (ui.projectId === id) { ui.savedAt = savedAt; ui.saveError = null; }
  } catch (e) {
    if (ui.projectId === id) ui.saveError = e instanceof ApiError ? e.message : 'The project file could not be saved.';
  }
  if (ui.projectId === id) writeBrowserCopy(savedAt);
}

/** Keep the service's own export records (it appends them) alongside ours. */
function mergeServer(ours: Project, theirs: Project): Project {
  const exports = [...ours.exports];
  for (const e of theirs.exports ?? []) if (!exports.some((x) => x.at === e.at && x.filename === e.filename)) exports.push(e);
  exports.sort((a, b) => b.at.localeCompare(a.at));
  return { ...ours, exports, analysis: { ...ours.analysis, rawResponsePath: ours.analysis.rawResponsePath ?? theirs.analysis?.rawResponsePath ?? null, usage: ours.analysis.usage ?? theirs.analysis?.usage ?? null } };
}

const debounced = debounce(() => { chain = chain.then(doSave); }, 1200);
/** Debounced autosave after a store event or a reference/analysis/job change. */
export function scheduleSave(): void {
  if (!ui.projectId || loading) return;
  debounced();
}
/** Save now (awaited): before an export, a new project or unload. */
export function flushSave(): Promise<void> {
  debounced.cancel();
  chain = chain.then(doSave);
  return chain;
}
export async function saveNow({ quiet = false }: { quiet?: boolean } = {}): Promise<void> {
  await flushSave();
  if (quiet) return;
  if (ui.saveError) toast(ui.saveError, { tone: 'warn' });
  else { toast(`Saved at ${fmtTime(ui.savedAt)}: project file and a copy on this device.`); announce('Saved.'); }
}

onStoreEvent((_s, e) => {
  if (['apply', 'undo', 'redo', 'commit', 'set', 'reset', 'restore', 'export'].includes(e.type)) scheduleSave();
});

/** beforeunload: write the browser copy now and try a keepalive PUT; warn when dirty or a job runs. */
export function onBeforeUnload(e: BeforeUnloadEvent): void {
  if (ui.projectId && ui.booted) {
    const pending = debounced.pending();
    debounced.cancel();
    const savedAt = new Date().toISOString();
    if (pending) {
      writeBrowserCopy(savedAt);
      const body = buildProject(savedAt, { historyLimit: 5 });
      if (JSON.stringify(body).length < 60_000) void api.putProject(ui.projectId, body, etag, { keepalive: true }).catch(() => null);
    }
  }
  if (app.store.isDirty && ui.booted && !ui.empty) { e.preventDefault(); e.returnValue = ''; }
}

// ---------------------------------------------------------------------------------------------
// Browser copy: offer, restore, clear
// ---------------------------------------------------------------------------------------------
export type BrowserCopy = { ok: true; snapshot: AutosaveSnapshot } | { ok: false; reason: string | null };
export function readBrowserCopy(id = ui.projectId): BrowserCopy {
  if (!id) return { ok: false, reason: null };
  const raw = lsGet(browserCopyKey(id));
  if (!raw) return { ok: false, reason: null };
  let parsed: unknown;
  try { parsed = JSON.parse(raw); } catch { return { ok: false, reason: 'The saved project could not be read.' }; }
  const v = validateSnapshot(parsed);
  return v.ok ? { ok: true, snapshot: parsed as AutosaveSnapshot } : { ok: false, reason: v.reason };
}
const contentHash = (s: { design: unknown; fields: unknown; revisions: { id: string }[]; currentRevisionId: string | null }) =>
  hashOf({ d: s.design, f: s.fields, r: s.revisions.map((r) => r.id), c: s.currentRevisionId });

function checkBrowserCopy(p: ServerProject): void {
  ui.restoreOffer = null;
  const copy = readBrowserCopy();
  if (!copy.ok) {
    if (copy.reason && ui.projectId) {
      lsDel(browserCopyKey(ui.projectId));
      setTimeout(() => toast('A saved project on this device could not be read, so it was cleared.', { tone: 'warn' }), 600);
    }
    return;
  }
  const st = copy.snapshot.state;
  const newer = Date.parse(copy.snapshot.savedAt) > Date.parse(p.savedAt) + 500;
  const differs = contentHash({ ...st, currentRevisionId: st.currentRevisionId ?? null }) !== contentHash({ design: p.working.design, fields: p.working.fields, revisions: p.revisions, currentRevisionId: p.working.parentRevisionId });
  if (newer && differs) ui.restoreOffer = { savedAt: copy.snapshot.savedAt };
}

export function restoreSaved(): void {
  const copy = readBrowserCopy();
  if (!copy.ok) {
    if (ui.projectId) lsDel(browserCopyKey(ui.projectId));
    ui.restoreOffer = null;
    toast('The saved project could not be read, so it was cleared. Your current work is unchanged.', { tone: 'warn' });
    announce('The saved project could not be read.');
    return;
  }
  const res = app.store.restore(copy.snapshot);
  if ('ok' in res && res.ok === false) {
    if (ui.projectId) lsDel(browserCopyKey(ui.projectId));
    ui.restoreOffer = null;
    toast('The saved project could not be read, so it was cleared. Your current work is unchanged.', { tone: 'warn' });
    return;
  }
  ui.sel = null;
  ui.restoreOffer = null;
  ui.empty = false;
  if (!['done', 'manual', 'review'].includes(ui.analysis.status)) ui.analysis = { ...ui.analysis, status: 'done' };
  toast(`Restored the version saved ${fmtDate(copy.snapshot.savedAt)}.`);
  announce('Restored the saved version.');
}

// ---------------------------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------------------------
export async function openProject(id: string, loaded?: { project: ServerProject; etag: string | null }): Promise<void> {
  const { project, etag: tag } = loaded ?? await api.getProject(id);
  loading = true;
  try {
    debounced.cancel();
    serverProject = project;
    etag = tag;
    ui.projectId = id;
    lsSet(LS_LAST, id);
    const w = project.working;
    const snapshot = {
      schemaVersion: SCHEMA_VERSION, savedAt: project.savedAt,
      state: { design: w.design, fields: w.fields, revisions: project.revisions, currentRevisionId: w.parentRevisionId, undoStack: w.undoStack, redoStack: w.redoStack, exports: project.exports ?? [] },
    };
    const res = app.store.restore(snapshot);
    if ('ok' in res && res.ok === false) {
      app.store.reset({ design: w.design, fields: w.fields, revisions: project.revisions, currentRevisionId: w.parentRevisionId, exports: project.exports ?? [] });
    }
    ui.ref = { url: project.reference?.sourceUrl ?? '', text: project.reference?.pastedText ?? '', images: refFromProject(id, project) };
    ui.analysis = analysisFromProject(project);
    ui.empty = project.analysis.status === 'none' && project.revisions.length === 0;
    ui.drafts = {};
    ui.fieldErrors = {};
    ui.checkEditing = null;
    ui.sel = null;
    ui.inspect = false;
    ui.explode = false;
    ui.bay = 0;
    ui.looked = false;
    ui.savedAt = project.savedAt;
    ui.saveError = null;
    ui.checks = project.checks ?? [];
    ui.checking = null;
    checkBrowserCopy(project);
    noteRegenerated(app.store.currentRevisionId);
  } finally {
    loading = false;
  }
}

/**
 * Plan D10: say when a revision is now built differently by a newer version of its style (its stored
 * hashes no longer match), so the next export saving a new revision is not a surprise.
 */
export function noteRegenerated(id: string | null): void {
  if (!id || app.store.revisionIdentity(id) !== 'regenerated') return;
  toast(`${id} is now built slightly differently by a newer version of its style. Your next export saves it as a new revision.`, { tone: 'warn' });
  announce(`${id} was rebuilt with a newer version of its style.`);
}

function idFrom(created: { project: ServerProject; location: string | null }): string | null {
  if (created.project.id) return created.project.id;
  const m = created.location?.match(/\/api\/projects\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]!) : null;
}

/** Create a project on the service and open it. The current project is saved first. */
export async function newProject({ from = 'blank', name = 'Untitled bookcase' }: { from?: ProjectFrom; name?: string } = {}): Promise<void> {
  if (ui.projectId) await flushSave();
  const created = await api.createProject(name, from);
  let id = idFrom(created);
  if (!id) {
    const list = await api.listProjects();
    id = list.slice().sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0]?.id ?? null;
  }
  if (!id) throw new ApiError(500, 'no-id', 'The new project could not be opened.');
  await openProject(id, { project: created.project, etag: created.etag });
  const u = new URL(location.href);
  u.searchParams.set('project', id);
  u.searchParams.delete('new');
  history.replaceState(null, '', u.toString());
}

// ---------------------------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------------------------
const mqDark = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;
export function setTheme(t: Theme): void {
  if (!['system', 'light', 'dark'].includes(t)) t = 'system';
  ui.theme = t;
  lsSet(LS_THEME, t);
  if (t === 'system') delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = t;
  ui.effectiveTheme = t === 'system' ? (mqDark?.matches ? 'dark' : 'light') : t;
}
mqDark?.addEventListener('change', () => { if (ui.theme === 'system') setTheme('system'); });

// ---------------------------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------------------------
export async function boot(): Promise<void> {
  setTheme((lsGet(LS_THEME) as Theme | null) ?? 'system');
  ui.bootError = null;
  try {
    const health = await api.health();
    ui.service = { intakeMode: health.intakeMode, version: health.version, demo: !!health.demo };
    try {
      const prof = loadProfiles(await api.profiles());
      if (prof.ok) setStore(createStore({ stock: prof.value.stock, construction: prof.value.construction }));
      else setStore(createStore());
    } catch {
      setStore(createStore());
    }
    const params = new URLSearchParams(location.search);
    let id = params.get('project');
    if (params.has('new')) {
      await newProject({ from: (params.get('new') as ProjectFrom) || 'blank' });
    } else {
      let loaded: { project: ServerProject; etag: string | null } | undefined;
      if (!id) id = lsGet(LS_LAST);
      if (id) { try { loaded = await api.getProject(id); } catch { id = null; } }
      if (!id) {
        const list = await api.listProjects().catch(() => []);
        id = list.slice().sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0]?.id ?? null;
        if (id) loaded = await api.getProject(id);
      }
      if (id && loaded) await openProject(id, loaded);
      else await newProject();
    }
    ui.booted = true;
    const initial = location.hash.slice(1);
    go(isStep(initial) ? initial : 'reference', { replace: true, focus: false });
  } catch (e) {
    ui.bootError = e instanceof Error ? e.message : 'The studio could not start.';
  }
}
